class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) inner.classList.remove('is-empty');
    this.productId = parsedState && parsedState.id;

    // Shopify sometimes answers /cart/add with sections:null. Rendering from that
    // used to throw, which left the customer with no drawer and no cart count even
    // though the item was added. Re-fetch the sections instead, and always open.
    const sections = parsedState && parsedState.sections;
    if (!sections) {
      this.refreshSectionsFromServer();
      return;
    }

    this.applySections(sections);
    this.finishRender();
  }

  applySections(sections) {
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const html = sections[section.id];
      if (!html) return;
      const innerHTML = this.getSectionInnerHTML(html, section.selector);
      if (innerHTML != null) sectionElement.innerHTML = innerHTML;
    });
  }

  refreshSectionsFromServer() {
    const ids = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    fetch(`${window.location.pathname}?sections=${ids}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => response.json())
      .then((data) => this.applySections(data))
      .catch(() => {})
      .finally(() => this.finishRender());
  }

  finishRender() {
    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.syncEmptyState();
      this.open();
    });
  }

  // The host's is-empty class is baked in by Liquid when the page renders, and
  // applySections only swaps the innerHTML of #CartDrawer - so after the first
  // add on a page that loaded with an empty cart the class is stale, and its CSS
  // hides the drawer header and the line that was just added. Re-derive it.
  syncEmptyState() {
    const hasLine = !!this.querySelector('.cart-item');
    this.classList.toggle('is-empty', !hasLine);
    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', !hasLine);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
